/* eslint-disable no-console */
import { Web3Provider } from "@ethersproject/providers";
import { logger } from "../../utils/logger";
import { CoinbaseWallet } from "@web3-react/coinbase-wallet";
import { useWeb3React as useEVMWallet } from "@web3-react/core";
import { Network } from "@web3-react/network";
import { Connector } from "@web3-react/types";
import { WalletConnect as WalletConnectV2 } from "@web3-react/walletconnect-v2";
import { useCallback, useMemo } from "react";
import { getChainName, getWalletProviderKind } from "../../config/chains";
import { ChainEnum, ChainIdEnum } from "../../enums/chain";
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { isSdkWalletProvider } from "../../enums/walletProvider";
import { CHAIN_EXPLORER_URLS, L2_CHAIN_EXPLORER_URLS } from "../../utils/chain";
import { isDevelopment } from "../../utils/env";
import { getAddChainParameters } from "../../utils/wallet/chains";
import {
  allConnectors,
  supportedChainId,
  supportedSigningChainIds,
  walletToConnector,
} from "../../utils/wallet/connectors";
import { EIP1193Connector } from "../../utils/wallet/connectors/eip6963";
import {
  forgetSelectedWallet,
  rememberSelectedWallet,
} from "../../utils/wallet/selectedWalletRecord";
import { SDK_KIND_TO_WALLET_ENUM, WalletEnum } from "../../utils/wallet/types";
import { useWalletAdapter } from "../../utils/wallet/providers/context";
import {
  ACTIVATE_NET_TIMEOUT_MS,
  withConnectTimeout,
} from "../../utils/wallet/providers/connectGuard";
import {
  bridgeProvider,
  unbridgeProvider,
} from "../../utils/wallet/providers/bridge";

export const allBrowserWallets = [
  WalletEnum.METAMASK,
  WalletEnum.BRAVE,
] as const;
export type IBrowserWalletType = (typeof allBrowserWallets)[number];

export interface IWallet {
  chainId: number | undefined;
  active: boolean;
  activate: (
    wallet: WalletEnum,
    eip6963Provider?: any,
    eip6963Rdns?: string
  ) => Promise<void>;
  deactivate: () => Promise<void>;
  account: string | null | undefined;
  connectedWallet: WalletEnum | undefined;
  provider: Web3Provider | undefined;
  hasBrowserWallet: boolean;
  connector?: Connector;
  isWrongNetwork: boolean;
  supportedChainId: ChainIdEnum;
  setChainToSwitch: (id: ChainIdEnum) => Promise<void>;
  explorerURL: string;
  l2ExplorerURL: string;
}

type ChainToId = {
  [key in ChainEnum]: ChainIdEnum;
};
export const CHAINS_TO_ID: ChainToId = {
  [ChainEnum.NotSelected]: ChainIdEnum.NONE,
  [ChainEnum.Ethereum]: ChainIdEnum.ETH_MAINNET,
  [ChainEnum.SepoliaTestnet]: ChainIdEnum.SEPOLIA_TESTNET,
  [ChainEnum.BasecampTestnet]: ChainIdEnum.BASECAMP_TESTNET,
  [ChainEnum.Optimism]: ChainIdEnum.OPTIMISM,
  [ChainEnum.OptimismTestnet]: ChainIdEnum.OPTIMISM_TESTNET,
  [ChainEnum.Arbitrum]: ChainIdEnum.ARBITRUM,
  [ChainEnum.ArbitrumTestnet]: ChainIdEnum.ARBITRUM_TESTNET,
  [ChainEnum.MegaethTestnet]: ChainIdEnum.MEGAETH_TESTNET,
  [ChainEnum.DogeosTestnet]:ChainIdEnum.DOGEOS_TESTNET,
  [ChainEnum.MonadTestnet]: ChainIdEnum.MONAD_TESTNET,
  [ChainEnum.ArcTestnet]: ChainIdEnum.ARC_TESTNET,
  [ChainEnum.BnbTestnet]: ChainIdEnum.BNB_TESTNET,
};

type IdToChain = {
  [key in ChainIdEnum]: string;
};
export const ID_TO_CHAINS: IdToChain = {
  [ChainIdEnum.NONE]: getChainName(ChainIdEnum.NONE),
  [ChainIdEnum.ETH_MAINNET]: getChainName(ChainIdEnum.ETH_MAINNET),
  [ChainIdEnum.SEPOLIA_TESTNET]: getChainName(ChainIdEnum.SEPOLIA_TESTNET),
  [ChainIdEnum.BASECAMP_TESTNET]: getChainName(ChainIdEnum.BASECAMP_TESTNET),
  [ChainIdEnum.OPTIMISM]: getChainName(ChainIdEnum.OPTIMISM),
  [ChainIdEnum.OPTIMISM_TESTNET]: getChainName(ChainIdEnum.OPTIMISM_TESTNET),
  [ChainIdEnum.ARBITRUM]: getChainName(ChainIdEnum.ARBITRUM),
  [ChainIdEnum.ARBITRUM_TESTNET]: getChainName(ChainIdEnum.ARBITRUM_TESTNET),
  [ChainIdEnum.MEGAETH_TESTNET]: getChainName(ChainIdEnum.MEGAETH_TESTNET),
  [ChainIdEnum.DOGEOS_TESTNET]: getChainName(ChainIdEnum.DOGEOS_TESTNET),
  [ChainIdEnum.MONAD_TESTNET]: getChainName(ChainIdEnum.MONAD_TESTNET),
  [ChainIdEnum.ARC_TESTNET]: getChainName(ChainIdEnum.ARC_TESTNET),
  [ChainIdEnum.BNB_TESTNET]: getChainName(ChainIdEnum.BNB_TESTNET),
};

const impersonateAddress = "";

export const useWallet = (): IWallet => {
  const {
    chainId: chainIdEth,
    account: accountEth,
    connector: connectorEth,
    isActive: isActiveEth,
    chainId: activeChainId,
    provider,
  } = useEVMWallet();

  // Active wallet backend for this chain (build-time constant via REACT_APP_CHAIN_ID).
  const walletKind = getWalletProviderKind();
  const isSdkKind = isSdkWalletProvider(walletKind);
  // The active SDK adapter controller (inert passthrough on WEB3REACT chains).
  const adapter = useWalletAdapter();

  // Returns the window's connector
  const windowEthereumObj = useMemo(() => {
    const windowObj: any | undefined = window as any;
    return windowObj?.ethereum;
  }, []);

  const connectedWallet = useMemo(() => {
    if (!connectorEth || !isActiveEth) {
      return undefined;
    }

    // On SDK chains the bridged connection is an EIP1193Connector, but we report
    // the specific SDK (Privy/Para/DogeOS) so the UI shows the right name/icon.
    if (isSdkKind) {
      return SDK_KIND_TO_WALLET_ENUM[walletKind] ?? WalletEnum.EIP6963;
    }

    if (connectorEth instanceof WalletConnectV2) {
      return WalletEnum.WALLETCONNECT;
    }
    if (connectorEth instanceof CoinbaseWallet) {
      return WalletEnum.WALLETLINK;
    }
    if (connectorEth instanceof EIP1193Connector) {
      return WalletEnum.EIP6963;
    }

    const isBraveWallet = !!windowEthereumObj?.isBraveWallet;
    if (isBraveWallet) {
      return WalletEnum.BRAVE;
    }
    return WalletEnum.METAMASK;
  }, [
    connectorEth,
    isActiveEth,
    windowEthereumObj?.isBraveWallet,
    isSdkKind,
    walletKind,
  ]);

  const setChainToSwitch = useCallback(
    async (id?: ChainIdEnum) => {
      // SDK chains: prefer the SDK controller's own switch (keeps SDK state in
      // sync, e.g. Privy). If the adapter doesn't implement it (Para/DogeOS),
      // fall back to the bridged EIP1193Connector's switch, which does
      // wallet_switchEthereumChain with the 4902 add-chain fallback.
      if (isSdkKind) {
        if (!id || id === activeChainId) {
          return;
        }
        try {
          if (adapter.switchChain) {
            await adapter.switchChain(id);
          } else if (connectorEth instanceof EIP1193Connector) {
            const params = getAddChainParameters(id);
            await connectorEth.switchChain(
              id,
              typeof params === "object" ? params : undefined
            );
          }
        } catch (switchError: any) {
          // Do nothing — wrong-network UI stays until the user switches.
        }
        return;
      }

      if (!id || id === activeChainId || !connectorEth) {
        return;
      }

      try {
        if (id === -1) {
          await connectorEth.activate();
        } else if (
          connectorEth instanceof WalletConnectV2 ||
          connectorEth instanceof Network
        ) {
          await connectorEth.activate(id);
        } else if (connectorEth instanceof EIP1193Connector) {
          const params = getAddChainParameters(id);
          await connectorEth.switchChain(
            id,
            typeof params === "object" ? params : undefined
          );
        } else {
          await connectorEth.activate(getAddChainParameters(id));
        }
      } catch (switchError: any) {
        // Do nothing
      }
    },
    [activeChainId, connectorEth, isSdkKind, adapter]
  );

  const deactivateEth = useCallback(async () => {
    localStorage.removeItem(LocalStorageKeyEnum.LAST_CONNECTED_WALLET);
    // Placed before the SDK early-return so it covers both kinds. Nothing is
    // selected after a disconnect, and a record left behind names a wallet whose
    // provider the deactivate below nulls -- which getActiveEip1193 refuses on.
    forgetSelectedWallet();

    // SDK chains: log out of the SDK (clears its own session) AND tear down the
    // web3-react bridge. Skipping the SDK logout would let a stale session
    // silently reconnect on the next mount.
    if (isSdkKind) {
      try {
        await adapter.disconnect();
      } catch (error) {
        logger.error("Error disconnecting SDK wallet", error);
      }
      await unbridgeProvider();
      return;
    }

    try {
      // Deactivate all connectors
      const promises = allConnectors.map(async (c) => {
        const connector = c[0];
        await connector.deactivate?.();
        await connector.resetState();
      });
      await Promise.all(promises);
    } catch (error) {
      logger.error("Error deactivating", error);
    }
  }, [isSdkKind, adapter]);

  const activate = useCallback(
    async (wallet: WalletEnum, eip6963Provider?: any, eip6963Rdns?: string) => {
      // Drop any previous EIP-6963 selection up front, before a single connector
      // is touched.
      //
      // This record is load-bearing: getActiveEip1193() REFUSES when it is set
      // and no provider is live, so a stale entry blocks every signature with
      // "reconnect <rdns>". Clearing it here rather than on the success paths
      // means no failure can strand one -- and the failures are real: when a
      // wallet is already active this function deactivates the other connectors
      // (nulling eip6963Connector.provider), `bridgeProvider` clears the connector
      // when an SDK bridge fails, and a rejected connect never adopts a provider.
      // Each can leave the connector empty while an old record survives.
      //
      // The connect modal re-writes it via rememberSelectedWallet() once an
      // EIP-6963 wallet has actually activated, which is the only state that
      // should produce a refusal.
      forgetSelectedWallet();

      // SDK chains: open the SDK's native modal, then bridge the resulting
      // provider into web3-react. Awaiting the bridge here means activate()
      // resolves only once the connection is active, so the connect-modal flow
      // advances at the right time. `bridgeProvider` dedupes against the reactive
      // <WalletBridge/>, so the two paths never double-activate.
      if (isSdkKind) {
        // The outermost net. This is the ONLY call site of `adapter.connect()`
        // in the app, which makes it the one place a bound can be applied that
        // no adapter — present or future — can opt out of.
        //
        // Adapters are contractually required to settle on their own (see the
        // `connect()` doc in providers/types.ts) and `usePendingConnect` gives
        // them the machinery to. This does not trust that. Two shipped adapters
        // already got it wrong, and the failure is invisible: a promise that
        // never settles emits no rejection, so nothing reaches Sentry and the
        // Connect Wallet button just dies.
        //
        // The window is longer than the adapters' own cap so their more
        // specific, better-instrumented rejection wins whenever both are armed.
        const {
          provider,
          account: sdkAccount,
          chainId: sdkChainId,
        } = await withConnectTimeout(
          adapter.connect(),
          ACTIVATE_NET_TIMEOUT_MS,
          () => ({ walletKind, adapterReady: adapter.isReady })
        );
        await bridgeProvider(provider, sdkAccount, sdkChainId);
        const sdkEnum = SDK_KIND_TO_WALLET_ENUM[walletKind];
        if (sdkEnum !== undefined) {
          localStorage.setItem(
            LocalStorageKeyEnum.LAST_CONNECTED_WALLET,
            String(sdkEnum)
          );
        }
        return;
      }

      // Deactivate any existing connection before switching wallets
      if (isActiveEth) {
        try {
          const promises = allConnectors.map(async (c) => {
            await c[0].deactivate?.();
            await c[0].resetState();
          });
          await Promise.all(promises);
        } catch (_) {
          // ignore deactivation errors
        }
      }

      // Connect to the selected connector
      const connector = walletToConnector[wallet as WalletEnum]();

      // For EIP-6963 wallets, inject the specific provider before activating
      if (wallet === WalletEnum.EIP6963 && eip6963Provider && connector instanceof EIP1193Connector) {
        connector.setProvider(eip6963Provider);
      }

      // If is wallet connect, we try to connect eagerly first
      // This is because activate() will do nothing if it
      // already has an active session
      if (connector instanceof WalletConnectV2) {
        // If theres an error here, we ignore and try to activate()
        await connector?.connectEagerly().catch(() => {});
      }
      if (connector instanceof WalletConnectV2) {
        await connector.activate(supportedChainId);
      } else {
        await connector?.activate(getAddChainParameters(supportedChainId));
      }
      localStorage.setItem(
        LocalStorageKeyEnum.LAST_CONNECTED_WALLET,
        String(wallet)
      );

      // THE SINGLE WRITER, and deliberately here rather than in the connect
      // modal. Two modal paths call activate() -- useModalPaging's wallet click
      // and useModalActions' CTA -- and only the first one wrote this record.
      // Since activate() clears it on entry, the CTA path left a user connected
      // through an EIP-6963 wallet with no record of it: after a reload
      // useEagerConnect had nothing to restore from, the connector stayed empty,
      // and getActiveEip1193 fell back to window.ethereum. That is the original
      // wrong-wallet defect, re-entered through the fix meant to close it, and
      // worse than before because the record is now actively deleted.
      //
      // Owning both the clear and the write in one function is what makes a third
      // call site unable to reintroduce it.
      if (wallet === WalletEnum.EIP6963 && eip6963Rdns) {
        rememberSelectedWallet(eip6963Rdns);
      }
    },
    [isActiveEth, isSdkKind, adapter, walletKind]
  );

  return {
    chainId: chainIdEth,
    active: isActiveEth,
    activate,
    deactivate: deactivateEth,
    account:
      isDevelopment() && impersonateAddress ? impersonateAddress : accountEth,
    connectedWallet,
    hasBrowserWallet: !!windowEthereumObj,
    provider: isActiveEth ? provider : undefined,
    connector: connectorEth,
    // If true, user is connected to an unsupported chain
    // Only enforces wrong network if not in development
    isWrongNetwork:
      Boolean(chainIdEth) &&
      !supportedSigningChainIds.includes(chainIdEth as ChainIdEnum),
    supportedChainId,
    setChainToSwitch,
    explorerURL: CHAIN_EXPLORER_URLS[supportedChainId],
    l2ExplorerURL: L2_CHAIN_EXPLORER_URLS[supportedChainId],
  };
};

export default useWallet;
